import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCalendars } from '@renderer/hooks/use-calendars'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'

/**
 * Round current time to the next hour
 */
function roundToNextHour(date: Date): Date {
  const rounded = new Date(date)
  rounded.setHours(rounded.getHours() + 1)
  rounded.setMinutes(0)
  rounded.setSeconds(0)
  rounded.setMilliseconds(0)
  return rounded
}

/**
 * Format date for Google Calendar URLs (YYYYMMDDTHHMMSS format)
 */
function formatGoogleDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${year}${month}${day}T${hours}${minutes}${seconds}`
}

/**
 * Determine which calendar provider to use based on the default account
 */
export function getProviderForAccount(
  defaultAccount: string | undefined,
  googleAccounts: Array<{ accountEmail: string }>,
  outlookAccounts: Array<{ accountEmail: string }>,
): 'google' | 'outlook' | null {
  if (!defaultAccount) {
    // Fallback: use first available provider, prefer Google
    if (googleAccounts.length > 0) return 'google'
    if (outlookAccounts.length > 0) return 'outlook'
    return null
  }

  if (googleAccounts.some((acc) => acc.accountEmail === defaultAccount)) {
    return 'google'
  }
  if (outlookAccounts.some((acc) => acc.accountEmail === defaultAccount)) {
    return 'outlook'
  }

  // Fallback if default not found
  if (googleAccounts.length > 0) return 'google'
  if (outlookAccounts.length > 0) return 'outlook'
  return null
}

/**
 * Generate a calendar creation URL with pre-filled date/time
 */
export function getCalendarCreateUrl(provider: 'google' | 'outlook', accountEmail?: string): string {
  const startTime = roundToNextHour(new Date())
  const endTime = new Date(startTime.getTime() + 60 * 60 * 1000) // 1 hour later

  if (provider === 'google') {
    const dates = `${formatGoogleDate(startTime)}/${formatGoogleDate(endTime)}`
    // Add authuser parameter to specify which Google account to use
    const authParam = accountEmail ? `&authuser=${encodeURIComponent(accountEmail)}` : ''
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&dates=${dates}${authParam}`
  } else {
    // Use outlook.live.com which may work better than outlook.office.com
    return `https://outlook.live.com/calendar/0/deeplink/compose?startdt=${startTime.toISOString()}&enddt=${endTime.toISOString()}`
  }
}

interface UseCreateCalendarEventOptions {
  trigger: string // Amplitude tracking trigger name
  fallbackPath?: string // Path to navigate if no calendars connected
}

/**
 * Hook that returns a handler to create a calendar event in the user's default calendar provider.
 * If no calendars are connected, it will navigate to the fallback path.
 */
export function useCreateCalendarEvent(options: UseCreateCalendarEventOptions): () => void {
  const navigate = useNavigate()
  const { defaultAccount, googleAccounts, outlookAccounts } = useCalendars()

  return useCallback(() => {
    // Check if any calendars are connected
    const hasCalendars = googleAccounts.length + outlookAccounts.length > 0

    if (!hasCalendars) {
      if (options.fallbackPath) {
        navigate(options.fallbackPath)
      }
      return
    }

    // Determine which provider to use
    const provider = getProviderForAccount(defaultAccount, googleAccounts, outlookAccounts)

    if (!provider) {
      if (options.fallbackPath) {
        navigate(options.fallbackPath)
      }
      return
    }

    // Track event
    AmplitudeRenderer.trackEvent('HLC Create Button Action', {
      action: 'Create Event',
      trigger: options.trigger,
      provider,
    })

    // Open calendar URL in external browser
    const url = getCalendarCreateUrl(provider, defaultAccount)
    window.open(url, '_blank')
  }, [navigate, defaultAccount, googleAccounts, outlookAccounts, options])
}
